import React, { useEffect } from 'react';
import styled from 'styled-components';
import Seo from '../components/common/Seo';
import { colors, breakpoints } from '../styles/variables';

const Page = styled.div`
  background-color: ${colors.white};
`;

const Hero = styled.section`
  background: linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 140%);
  color: ${colors.white};
  padding: 136px 24px 72px;
`;

const HeroInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const Tag = styled.span`
  display: inline-block;
  background: rgba(255, 255, 255, 0.15);
  color: ${colors.white};
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 16px;
`;

const Title = styled.h1`
  font-size: 44px;
  font-weight: 700;
  line-height: 1.25;
  margin: 0 0 16px;

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 32px;
  }
`;

const Lead = styled.p`
  font-size: 18px;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.9);
  max-width: 760px;
  margin: 0;
`;

const Section = styled.section`
  max-width: 1100px;
  margin: 0 auto;
  padding: 80px 24px;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 56px 24px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 30px;
  font-weight: 700;
  color: ${colors.primary};
  margin: 0 0 12px;
`;

const SectionLead = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: ${colors.textGray};
  max-width: 760px;
  margin: 0 0 36px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 24px;
`;

const Card = styled.div`
  background: ${colors.lightGray};
  border-radius: 12px;
  padding: 28px;

  h3 {
    font-size: 18px;
    font-weight: 700;
    color: ${colors.primary};
    margin: 0 0 12px;
  }

  p {
    font-size: 15px;
    line-height: 1.7;
    color: ${colors.textGray};
    margin: 0;
  }
`;

const ImageRow = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 32px;
  align-items: center;

  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const StudyImage = styled.img`
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: 12px;
  box-shadow: 0 18px 42px rgba(27, 28, 29, 0.14);
`;

const Steps = styled.ol`
  counter-reset: step;
  list-style: none;
  padding: 0;
  display: grid;
  gap: 16px;

  li {
    counter-increment: step;
    background: ${colors.white};
    border: 1px solid ${colors.lightGray};
    border-radius: 12px;
    padding: 24px 28px 24px 80px;
    position: relative;

    &::before {
      content: counter(step);
      position: absolute;
      left: 24px;
      top: 24px;
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: ${colors.secondary};
      color: ${colors.white};
      font-weight: 700;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 15px;
    }

    h3 {
      font-size: 17px;
      font-weight: 700;
      color: ${colors.primary};
      margin: 0 0 6px;
    }

    p {
      font-size: 14.5px;
      line-height: 1.7;
      color: ${colors.textGray};
      margin: 0;
    }
  }
`;

const AboutPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Page>
      <Seo
        title="클럽 소개"
        description="ElectroWave Club은 학원이 아닌 1:1 멘토 매칭 스터디 클럽입니다. 학생과 멘토를 줌(Zoom)으로 연결해 작은 프로젝트를 끝까지 완성하며 함께 성장합니다."
        path="/about"
        ogType="website"
      />

      <Hero>
        <HeroInner>
          <Tag>About ElectroWave Club</Tag>
          <Title>학원이 아닌, 멘토와 학생을{'\n'}연결해 주는 스터디 클럽</Title>
          <Lead>
            ElectroWave Club은 정해진 커리큘럼을 일방적으로 전달하는 학원이 아닙니다.
            배우고 싶은 학생과 가르쳐 보고 싶은 현업 멘토를 짝지어 주고, 작은 프로젝트를 끝까지 함께 완성하도록
            돕는 스터디 클럽입니다. 학습의 주도권은 언제나 학생에게 있고, 멘토는 옆에서 코드 리뷰와 길잡이로
            함께합니다.
          </Lead>
        </HeroInner>
      </Hero>

      <Section>
        <ImageRow>
          <div>
            <SectionTitle>우리는 어떤 곳인가</SectionTitle>
            <SectionLead>
              ElectroWave Club은 영상 강의를 팔지 않습니다. 우리는 실제로 한 사람의 개발자와 한 사람의 학생을
              연결해, 같은 프로젝트를 함께 들여다보는 시간을 만듭니다. 회비는 멘토 매칭과 운영을 위한 최소한의
              비용이며, 그 외 학습 결과물(코드, 프로젝트, 포트폴리오)은 모두 학생의 것입니다.
            </SectionLead>
          </div>
          <StudyImage
            src="/assets/images/about-study-session.png"
            alt="자연광이 들어오는 공간에서 멘토가 학생에게 노트북 화면의 코드를 설명하는 모습"
          />
        </ImageRow>
      </Section>

      <Section style={{ background: colors.lightGray, maxWidth: 'none', padding: '80px 24px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <SectionTitle>매칭은 이렇게 진행됩니다</SectionTitle>
          <SectionLead>
            상담 신청부터 첫 코드 리뷰까지, 클럽이 학생과 멘토를 연결하는 과정입니다.
          </SectionLead>
          <Steps>
            <li>
              <h3>학습 목표 정리 & 상담 신청</h3>
              <p>
                무엇을 배우고 싶은지, 지금 어디까지 와 있는지 간단히 적어 상담을 신청합니다. 어떤 코스로
                들어갈지 모르겠으면 그 상태 그대로 적어 주셔도 됩니다.
              </p>
            </li>
            <li>
              <h3>운영자가 멘토 후보 매칭</h3>
              <p>
                운영자가 신청 내용을 보고 가장 잘 맞을 만한 멘토 1~2명을 후보로 제안합니다. 멘토의 경력, 작업
                스타일, 가능 시간대를 함께 안내드립니다.
              </p>
            </li>
            <li>
              <h3>멘토와 학생의 첫 미팅</h3>
              <p>
                후보 멘토와 1회 무료 오리엔테이션을 진행하면서 학습 방향과 프로젝트 주제를 같이 정합니다.
                결이 잘 맞지 않으면 다른 멘토로 바꿀 수 있습니다.
              </p>
            </li>
            <li>
              <h3>주간 페이스로 함께 진행</h3>
              <p>
                주 1회 정기 코드 리뷰 + 평일 비동기 Q&A 형태로 진행합니다. 매주 끝나는 작은 마일스톤을 잡고,
                학습 일지를 함께 정리합니다.
              </p>
            </li>
            <li>
              <h3>포트폴리오로 마무리</h3>
              <p>
                과정의 끝에는 GitHub 저장소와 라이브 데모, 그리고 프로젝트의 의도와 의사결정을 담은 정리
                글이 남습니다. 그 자체가 자기소개서가 됩니다.
              </p>
            </li>
          </Steps>
        </div>
      </Section>

      <Section>
        <SectionTitle>운영 원칙</SectionTitle>
        <SectionLead>
          이 클럽이 다른 학원·강의 플랫폼과 가장 크게 다른 부분입니다.
        </SectionLead>
        <Grid>
          <Card>
            <h3>① 학생 1명에게 멘토 1명</h3>
            <p>
              그룹 강의가 아닌 1:1 매칭이 기본입니다. 같은 코스를 듣는 사람들이 모이는 라운지가 따로 있어,
              필요할 때 서로의 코드를 봐 주는 동료 학습도 함께 일어납니다.
            </p>
          </Card>
          <Card>
            <h3>② 영상 강의 0%, 코드 리뷰 100%</h3>
            <p>
              미리 만들어 둔 영상은 없습니다. 학생이 짠 코드와 멘토가 함께 다시 짠 코드, 그 사이의 차이가
              학습 자료입니다.
            </p>
          </Card>
          <Card>
            <h3>③ 도구는 가리지 않는다</h3>
            <p>
              AI 코드 도우미, IDE, 프레임워크 — 실제 현장에서 쓰는 도구를 그대로 사용합니다. 비효율적인
              금지보다 현명한 활용을 가르칩니다.
            </p>
          </Card>
          <Card>
            <h3>④ 학생이 만든 결과물은 학생의 것</h3>
            <p>
              학생이 직접 작성한 코드·문서·포트폴리오의 저작권은 학생에게 있습니다. 멘토 자료를 참고해
              만든 결과물 또한 학생의 자산이며, 공개·비공개는 학생이 결정합니다.
            </p>
          </Card>
          <Card>
            <h3>⑤ 멘토가 제공한 자료의 소유권은 멘토에게</h3>
            <p>
              멘토가 학습용으로 제공한 예제 코드·문서·슬라이드 등 콘텐츠의 소유권은 멘토에게 귀속합니다.
              해당 자료는 개인 학습 목적으로만 사용할 수 있으며, <strong>멘토의 사전 동의 없이 외부 공개·
              복제·재배포할 수 없습니다.</strong>
            </p>
          </Card>
        </Grid>
      </Section>

      <Section>
        <SectionTitle>이런 분들과 잘 맞습니다</SectionTitle>
        <SectionLead>
          정해진 자격은 없습니다. 다음과 같은 분들이 가장 도움을 많이 받았습니다.
        </SectionLead>
        <Grid>
          <Card>
            <h3>전공 1·2학년</h3>
            <p>전공 수업과 별개로 실제로 작동하는 코드를 써 보고 싶은 학생.</p>
          </Card>
          <Card>
            <h3>비전공 입문자</h3>
            <p>책으로만 배우다가 막힌, 진짜 프로젝트로 넘어가고 싶은 직장인·전공 전환자.</p>
          </Card>
          <Card>
            <h3>현업 주니어</h3>
            <p>회사에서 1~2년 차, 새로운 스택으로 영역을 넓히고 싶은 분.</p>
          </Card>
          <Card>
            <h3>가르쳐 보고 싶은 시니어</h3>
            <p>현업 경험을 멘토로서 정리하고 싶은 5년 차 이상의 개발자도 환영합니다.</p>
          </Card>
        </Grid>
      </Section>
    </Page>
  );
};

export default AboutPage;
